import React from 'react';
import {
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  Box,
} from '@mui/material';
import dayjs from 'dayjs';
import ValidatedTextField from '../../../Components/ValidatedTextField';
import DateTimePicker from '../../../Components/DateTimePicker';
import { numberValidator, nameValidator } from '../../../utils/validators';
import TooltipWrapper from '../../../Components/TooltipWrapper';
import { toolTips } from '../../../utils/questionToolTips';
import { IRSQuestion } from '../../../types/irsForms';
import IRSFormOtherCategory from './IRSFormOtherCategory';

interface IRSFormQuestionProps {
  question: IRSQuestion;
  answer: string | number | boolean | null | undefined;
  onChange: (value: string | number | boolean | null) => void;
  onFormChange?: (isValid: boolean) => void;
  disabled?: boolean;
  errorOverride?: string | null;
  parentAnswer?: string | number | boolean | null;
  formId: string;
  onOtherItemsUpdate?: () => void;
}

const IRSFormQuestion: React.FC<IRSFormQuestionProps> = ({
  question,
  answer,
  onChange,
  onFormChange,
  disabled = false,
  errorOverride = null,
  parentAnswer,
  formId,
  onOtherItemsUpdate,
}) => {
  const handleChange = (isValid: boolean, value: any) => {
    if (onFormChange) {
      onFormChange(isValid);
    }
    onChange(value);
  };

  const handleBooleanChange = (value: string) => {
    if (value === 'true') {
      onChange(true);
    } else if (value === 'false') {
      onChange(false);
    } else {
      onChange(null);
    }
  };

  // Check if this conditional question should be shown
  const shouldShow = () => {
    if (!question.is_conditional || !question.parent_question_id) {
      return true;
    }

    if (parentAnswer === null || parentAnswer === undefined) {
      return false;
    }

    // For boolean parents, check if answer matches conditional_value
    if (typeof parentAnswer === 'boolean') {
      const conditionalValue = question.conditional_value;
      if (conditionalValue === '1' || conditionalValue === 'true') {
        return parentAnswer === true;
      }
      if (conditionalValue === '0' || conditionalValue === 'false') {
        return parentAnswer === false;
      }
      return String(parentAnswer) === conditionalValue;
    }

    // For other types, compare as strings
    return String(parentAnswer) === question.conditional_value;
  };

  if (!shouldShow()) {
    return null;
  }

  const renderAnswerInput = () => {
    switch (question.answer_type) {
      case 'decimal':
        return (
          <ValidatedTextField
            label=""
            validator={numberValidator}
            startAdornment={`${question.question_number || ''}. $`}
            errorOverride={errorOverride}
            disabled={disabled}
            number={true}
            financial={true}
            value={answer as number | null | undefined}
            onChange={(isValid, value) => handleChange(isValid, value)}
          />
        );

      case 'text':
        return (
          <ValidatedTextField
            label=""
            validator={nameValidator}
            startAdornment={`${question.question_number || ''}.`}
            errorOverride={errorOverride}
            disabled={disabled}
            value={answer as string | null | undefined}
            onChange={(isValid, value) => handleChange(isValid, value)}
          />
        );

      case 'date':
        // DateTimePicker doesn't support disabled prop directly, so we'll handle it differently
        return (
          <Box
            sx={{
              pointerEvents: disabled ? 'none' : 'auto',
              opacity: disabled ? 0.6 : 1,
            }}
          >
            <DateTimePicker
              date={answer ? dayjs(answer as string) : null}
              label={`${question.question_number || ''}.`}
              minDate={null}
              maxDate={null}
              handleDateChange={(value) =>
                onChange(value ? value.format('YYYY-MM-DD') : null)
              }
              extraError={false}
              extraErrorMessage=""
              handleErrorUpdate={() => {}}
              shown={true}
              disableFuture={false}
              required={question.is_required}
            />
          </Box>
        );

      case 'boolean':
        return (
          <FormControl
            size="small"
            fullWidth
            error={!!errorOverride}
            disabled={disabled}
          >
            <Select
              value={
                answer === null || answer === undefined
                  ? ''
                  : answer === true
                    ? 'true'
                    : 'false'
              }
              onChange={(event) => handleBooleanChange(event.target.value)}
            >
              <MenuItem value="">Select</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
            {!!errorOverride && (
              <FormHelperText>{errorOverride}</FormHelperText>
            )}
          </FormControl>
        );

      case 'other_category':
        return (
          <IRSFormOtherCategory
            formId={formId}
            questionId={question.question_id}
            items={question.other_line_items || []}
            editable={!disabled}
            onUpdate={onOtherItemsUpdate || (() => {})}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Grid container sx={{ mb: 2 }}>
      <Grid item xs={9} sx={{ p: 2 }}>
        <Typography variant="body1">
          <b>{question.question_number || ''}.</b> {question.question_text}{' '}
          {question.is_required && (
            <Typography component="span" color="error">
              *
            </Typography>
          )}{' '}
          {question.question_id &&
            Object.keys(toolTips).includes(question.question_id) && (
              <TooltipWrapper
                message={toolTips[question.question_id]}
                id={question.question_id}
              />
            )}
        </Typography>
      </Grid>
      {question.answer_type !== 'other_category' && (
        <Grid item xs={3} sx={{ p: 2 }}>
          {renderAnswerInput()}
        </Grid>
      )}
      {question.answer_type === 'other_category' && (
        <Grid item xs={12} sx={{ p: 2 }}>
          {renderAnswerInput()}
        </Grid>
      )}
    </Grid>
  );
};

export default IRSFormQuestion;
